import { Segment } from '@dna/analytics'
import { WithProfiler } from '@dna/mixins'
import * as paths from '@dna/paths'
import styles from '@dna/styles'
import { t } from '@dna/utils'
import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  repeat,
} from '@lit'

/**
 * attachinternals to create a form-associated custom element is not supported in safari 16
 * The below is a polyfill and can be removed once safari 16 is no longer supported
 */
import '@polyfill/element-internals'

/**
 * Mixin that adds form-associated behavior to a custom element.
 */

export const FormAssociatedMixin = (superclass) =>
  class extends superclass {
    static formAssociated = true
    static properties = {
      filter: {
        type: Object,
      },
      selectedOptions: {
        type: Array,
      },
    }

    constructor() {
      super()
      this.internals_ = this.attachInternals()
      this.selectedOptions = this.value || []
      this.setFormValue(this.selectedOptions)
    }

    get value() {
      return this.selectedOptions
    }

    set value(value) {
      this.selectedOptions = value
      this.internals_.setFormValue(value)
    }

    setFormValue(value) {
      this.internals_.setFormValue(value)
      this.dispatchEvent(new Event('change', { bubbles: true }))
    }

    selectOption(option) {
      if (this.selectedOptions.includes(option.value)) {
        this.selectedOptions = this.selectedOptions.filter(
          (selectedOption) => selectedOption !== option.value,
        )
        this.setFormValue(this.selectedOptions)

        // @segment
        Segment.track(Segment.CONSTANTS.EVENTS.FILTER_REMOVED, {
          filter_name: option?.name?.toLowerCase(),
          filter_type: this.filter.display_name?.toLowerCase(),
        })
      } else {
        this.selectedOptions = [...this.selectedOptions, option.value]
        this.setFormValue(this.selectedOptions)

        // @segment
        Segment.protect(() => {
          if (this.selectedOptions.length) {
            Segment.track(Segment.CONSTANTS.EVENTS.FILTER_ADDED, {
              filter_name: option?.name?.toLowerCase(),
              filter_type: this.filter.display_name?.toLowerCase(),
            })
          }
        })
      }
    }
  }

/**
 * Color and Pattern filter
 */

class DNAColorFilter extends FormAssociatedMixin(LitElement) {
  constructor() {
    super()
    this.filter = {
      options: [],
    }
  }

  render() {
    return html`
      <div class="radio-group" role="radiogroup" aria-label=${t('sections.collection_template.filter_accessible_text')}>
        ${this.filter?.options?.map((option) => {
          const swatchFilename = `/cdn/shop/files/filter_${this.filter.name}_${option.value}.png`

          return html`
            <button class=${classMap({
              'color-swatch': true,
              selected: this.selectedOptions.includes(option.value),
            })}  @click=${() => this.selectOption(option)}
              role="radio"
              aria-checked=${this.selectedOptions.includes(option.value)}
            >

              <img class='color' height="24" width="24" src="${swatchFilename}" alt="" loading="lazy" />
              <span class="label p2">${option.name} (${option.count})</span>
            </button>
          `
        })}
      </div>
    `
  }

  static styles = [
    styles.typography,
    css`
      .radio-group {
        display: inline-grid;
        width: 100%;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 24px 8px;
        padding:  4px 4px;
      }

      /* breakpoint-tablet */
      @media screen and (min-width: 768px) {
        .radio-group {
          gap: 20px 8px;
        }
      }

      .color-swatch {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 0;
        border: 0;
        background-color: transparent;
        cursor: pointer;
      }

      .color {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        object-fit: cover;
      }

      .selected .color {
        outline: 1px solid var(--dna-color-midnight);
        outline-offset: 2px;
      }

      .label {
        color: var(--dna-brand-primary);
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `,
  ]
}
customElements.define('dna-color-filter', DNAColorFilter)

/**
 * Size filter
 */

class DNASizeFilter extends FormAssociatedMixin(LitElement) {
  constructor() {
    super()
    this.filter = {
      options: [],
    }
  }

  render() {
    return html`
      <div class="radio-group" role="radiogroup" aria-label=${t('sections.collection_template.filter_accessible_text')}>
        ${this.filter?.options?.map((option) => {
          return html`
            <button class=${classMap({
              size: true,
              selected: this.selectedOptions.includes(option.value),
            })}  @click=${() => this.selectOption(option)}
              role="radio"
              aria-checked=${this.selectedOptions.includes(option.value)}
              >

              <span class="label p2">${option.name}</span>
            </button>
          `
        })}
      </div>
    `
  }

  static styles = [
    styles.typography,
    css`
      .radio-group {
        display: inline-grid;
        grid-template-columns: repeat(6, 1fr);
        justify-content: flex-start;
        gap: 8px 8px;
      }


      .size {
        width: 48px;
        height: 48px;
        background-color: transparent;
        border: 1px solid var(--dna-color-light-grey);
        box-sizing: border-box;
        cursor: pointer;
      }

      /* breakpoint-tablet */
      @media screen and (min-width: 768px) {
        .size {
          width: 40px;
          height: 40px;
        }
      }

      .selected.size {
        border: 2px solid var(--dna-color-midnight);
      }

      .label {
        color: var(--dna-brand-primary);
      }
    `,
  ]
}
customElements.define('dna-size-filter', DNASizeFilter)

/**
 * Type 'Single' filter
 */

class DNASingleFilter extends FormAssociatedMixin(LitElement) {
  constructor() {
    super()
    this.filter = {
      options: [],
    }
  }

  chooseOption(option) {
    const filterOption = this.filter.options.find(
      (opt) => opt.value === option.value,
    )
    this.selectedOptions = []
    option.name = filterOption?.name.toLowerCase()
    this.selectOption(option)
  }

  render() {
    const selectedOption =
      this.filter.options.find((option) =>
        this.selectedOptions.includes(option.value),
      ) || ''
    return html`
      <sl-radio-group class="radio-group" value="${selectedOption.value}" @sl-change=${(event) => this.chooseOption({ value: event.target.value })}>
        ${repeat(
          this.filter?.options || [],
          (option) => option.value,
          (option) => html`
            <sl-radio value="${option.value}">
              ${option.name} (${option.count})
            </sl-radio>
          `,
        )}
      </sl-radio-group>
    `
  }

  static styles = [
    styles.typography,
    styles.shoelace,
    css`
      .radio-group {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding:  0 4px;
      }

      /* breakpoint-tablet */
      @media screen and (min-width: 768px) {
        .radio-group {
          gap: 16px;
        }
      }
    `,
  ]
}
customElements.define('dna-single-filter', DNASingleFilter)

/**
 * Default Select filter
 */

class DNADefaultFilter extends FormAssociatedMixin(LitElement) {
  static properties = {
    showAllOptions: { type: Boolean },
  }

  constructor() {
    super()
    this.filter = {
      options: [],
    }
    this.showAllOptions = false
  }

  get visibleOptions() {
    const initialOptions = this.filter?.options.slice(6)
    const selectedHiddenInitialOption = initialOptions.some((option) =>
      this.selectedOptions.includes(option.value),
    )
    if (selectedHiddenInitialOption || this.showAllOptions) {
      this.showAllOptions = true
      return this.filter?.options
    }
    return this.filter?.options.slice(0, 6)
  }

  chooseOption(option) {
    this.selectOption(option)
  }

  handleCheckboxFocus(event) {
    const focusedCheckbox = event.target

    this.handleCheckboxKeydown = (event) => {
      if (event.key === 'Enter') {
        focusedCheckbox.click()
      }
    }

    focusedCheckbox.addEventListener('keydown', this.handleCheckboxKeydown)
  }

  handleCheckboxFocusRemoval(event) {
    const focusedCheckbox = event.target
    focusedCheckbox.removeEventListener('keydown', this.handleCheckboxKeydown)
  }

  toggleShowAllOptions() {
    this.showAllOptions = true
  }

  render() {
    return html`
      <div class="radio-group" role="radiogroup" aria-label=${t('sections.collection_template.filter_accessible_text')}>
        ${this.visibleOptions?.map(
          (option) => html`
           <sl-checkbox
           .checked=${this.selectedOptions.includes(option.value)}
            @sl-change=${() => this.chooseOption(option)}
            @sl-focus=${(event) => this.handleCheckboxFocus(event)}
            @sl-blur=${(event) => this.handleCheckboxFocusRemoval(event)}
            .value="${option.value}" name="${option.name}"
            role="radio"
            aria-checked=${this.selectedOptions.includes(option.value)}
           >${option.name} (${option.count})</sl-checkbox>
          `,
        )}
        ${
          this.filter?.options?.length > 6 && !this.showAllOptions
            ? html`
          <button class="view-more-button" @click=${this.toggleShowAllOptions}>
            ${t('sections.collection_template.view_more_button')}
          </button>
        `
            : nothing
        }
      </div>
    `
  }

  static styles = [
    styles.typography,
    styles.shoelace,
    css`
      .radio-group {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding:  0 4px;
      }

      /* breakpoint-tablet */
      @media screen and (min-width: 768px) {
        .radio-group {
          gap: 16px;
        }
      }
      .view-more-button {
        background: transparent;
        border: none;
        text-decoration: underline;
        padding: 0;
        text-align: left;
        cursor: pointer;
        color: var(--dna-color-midnight);
      }
    `,
  ]
}
customElements.define('dna-default-filter', DNADefaultFilter)

class DNAGridFilters extends WithProfiler(LitElement) {
  static properties = {
    filters: {
      type: Array,
    },
    hidePriceControlOptions: {
      type: Boolean,
    },
  }

  $form = createRef()

  constructor() {
    super()
    this.filters = []
    this.hidePriceControlOptions = false

    this.router = __DNA_GLOBAL__.utils.queryRouter
  }

  _handleFormChange(event) {
    event.preventDefault()
    const formData = new FormData(this.$form.value)
    const data = Object.fromEntries(formData.entries())

    const prefixedData = Object.keys(data).reduce((acc, key) => {
      acc[`filter.${key}`] = data?.[key] ? data[key].split(',') : []
      return acc
    }, {})

    this.router.query.update({
      ...prefixedData,
      page: 1, // On filter change, reset the page to 1
    })
  }

  showFilter(filter, selected) {
    if (filter.type === 'single') {
      return html`
        <dna-single-filter .filter=${filter} name="${filter.name}" .value=${selected}></dna-single-filter>
      `
    }
    switch (filter.name) {
      case 'color':
      case 'pattern':
        return html`
          <dna-color-filter .filter=${filter} name="${filter.name}" .value=${selected}></dna-color-filter>
        `
      case 'size':
        return html`
          <dna-size-filter .filter=${filter} name="${filter.name}" .value=${selected}></dna-size-filter>
        `
      default:
        return html`
          <dna-default-filter .filter=${filter} name="${filter.name}" .value=${selected}></dna-default-filter>
        `
    }
  }

  render() {
    return html`
      <div class="container">
        <form
          class="filter-group"
          ${ref(this.$form)}
          @change=${this._handleFormChange}
        >
          ${repeat(
            this.filters,
            (filter) => filter.display_name,
            (filter, index) => {
              if (
                !filter?.options ||
                (this.hidePriceControlOptions &&
                  filter.display_name === 'PRICE')
              ) {
                return nothing
              }
              const selectedValues = filter?.options
                ?.filter((option) => {
                  return option.status === 'selected'
                })
                .map((option) => option.value)

              return html`
                <sl-details
                  ?open=${index === 0}
                  value=${selectedValues}
                >
                <div class="filter-group__summary" slot="summary">${filter.display_name}</div>
                  <span slot="expand-icon">
                    ${paths.plus}
                  </span>
                  <span slot="collapse-icon">
                    ${paths.minus}
                  </span>
                  ${this.showFilter(filter, selectedValues)}
                </sl-details>
              `
            },
          )}
        </form>
      </div>
    `
  }

  static styles = [
    styles.container,
    styles.typography,
    css`
      * {
        box-sizing: border-box;
      }

      .container {
        padding-left: 20px;
        padding-right: 20px;
      }

     sl-details::part(base) {
        border: none;
        border-top: 1px solid var(--dna-color-light-grey);
        border-radius: 0;
        background-color: transparent;
      }

      sl-details:first-child::part(base) {
        border-top: none;
      }

      sl-details::part(summary-icon) {
        /* Disable the expand/collapse animation */
        rotate: none;
        font-size: 24px;
      }

      sl-details::part(header) {
        padding: 20px 0;
        --sl-focus-ring: 1px solid var(--dna-color-focus);
        --sl-focus-ring-offset: -2px;
        font: var(--dna-label-m-font);
        letter-spacing: 2px;
        text-transform: uppercase;
      }

      /* breakpoint-tablet */
      @media screen and (min-width: 1024px) {
        sl-details::part(header) {
          font: var(--dna-label-s-font)
        }
      }

      sl-details::part(content) {
        padding: 0 0 20px 0;
      }
      .filter-group__summary {
        font-weight: normal;
        margin: 0;
      }
    `,
  ]
}

customElements.define('dna-grid-filters', DNAGridFilters)
